import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MessageCircle, RefreshCw, X } from "lucide-react";
import ChatMessage from "./ChatMessage";
import ChatComposer from "./ChatComposer";
import { cn } from "@/lib/utils";

const QUICK_REPLIES = [
    "How much for a fence?",
    "Do you do decks?",
    "What areas do you serve?",
    "Get a free quote",
];

const TypingIndicator = () => (
    <div className="flex gap-1 px-3 py-2.5" aria-label="Assistant is typing" data-testid="chat-typing">
        <span className="h-2 w-2 bg-brand-gray rounded-full animate-bounce [animation-delay:-0.32s]" />
        <span className="h-2 w-2 bg-brand-gray rounded-full animate-bounce [animation-delay:-0.16s]" />
        <span className="h-2 w-2 bg-brand-gray rounded-full animate-bounce" />
    </div>
);

const ChatPanel = ({
    open,
    onClose,
    messages,
    sending,
    sessionStatus,
    error,
    handoffService,
    sessionId,
    onSend,
    onClear,
}) => {
    const [draft, setDraft] = useState("");
    const scrollRef = useRef(null);

    useEffect(() => {
        if (open && scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages, sending, open]);

    if (!open) return null;

    const userHasSent = messages.some((m) => m.role === "user");
    const closed = sessionStatus !== "active";

    const handleSend = () => {
        const text = draft.trim();
        if (!text) return;
        setDraft("");
        onSend(text);
    };

    const handleQuickReply = (text) => {
        setDraft("");
        onSend(text);
    };

    const handoffHref = handoffService
        ? `/contact?service=${
              handoffService === "general" ? "" : handoffService
          }&utm_source=chat${sessionId ? `&chat_session=${sessionId}` : ""}`.replace(
              "service=&",
              "",
          )
        : null;

    return (
        <div
            className={cn(
                // Mobile: full-screen.  Desktop: floating panel bottom-right.
                "fixed z-[60] bg-white shadow-2xl border border-brand-dark/10 flex flex-col",
                "inset-0 md:inset-auto md:bottom-6 md:right-6 md:w-[380px] md:h-[600px]",
            )}
            role="dialog"
            aria-label="Reformity chat assistant"
            data-testid="chat-panel"
        >
            {/* Header */}
            <div className="bg-brand-dark text-white px-4 py-3 flex items-center justify-between gap-2 shrink-0">
                <div className="flex items-center gap-2 min-w-0">
                    <span className="inline-flex h-8 w-8 items-center justify-center bg-brand-cyan/15 text-brand-cyan">
                        <MessageCircle className="h-4 w-4" strokeWidth={2.25} />
                    </span>
                    <div className="min-w-0">
                        <p className="font-display text-base tracking-wide leading-none">
                            Chat with Reformity
                        </p>
                        <p className="text-[10px] uppercase tracking-wider text-brand-cyan/80 mt-1">
                            {closed ? "Session closed" : "Online · usually replies in seconds"}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                    <button
                        type="button"
                        onClick={onClear}
                        className="p-2 text-white/70 hover:text-white transition-colors"
                        aria-label="Clear chat"
                        title="Start over"
                        data-testid="chat-clear-button"
                    >
                        <RefreshCw className="h-4 w-4" strokeWidth={2.25} />
                    </button>
                    <button
                        type="button"
                        onClick={onClose}
                        className="p-2 text-white/70 hover:text-white transition-colors"
                        aria-label="Close chat"
                        data-testid="chat-close-button"
                    >
                        <X className="h-5 w-5" strokeWidth={2.25} />
                    </button>
                </div>
            </div>

            {/* Body */}
            <div
                ref={scrollRef}
                className="flex-1 overflow-y-auto px-3 py-4 bg-white space-y-3"
                data-testid="chat-message-list"
            >
                {messages.map((m, i) => (
                    <ChatMessage key={`${m.role}-${i}-${m.timestamp || ""}`} message={m} index={i} />
                ))}

                {sending && (
                    <div className="flex justify-start" data-testid="chat-typing-wrapper">
                        <div className="bg-brand-offwhite border-l-4 border-brand-cyan">
                            <TypingIndicator />
                        </div>
                    </div>
                )}

                {!userHasSent && !sending && (
                    <div className="pt-2 flex flex-wrap gap-2" data-testid="chat-quick-replies">
                        {QUICK_REPLIES.map((q, i) => (
                            <button
                                key={q}
                                type="button"
                                onClick={() => handleQuickReply(q)}
                                className="text-xs font-semibold uppercase tracking-wider border border-brand-cyan/40 text-brand-cyan px-3 py-1.5 hover:bg-brand-cyan hover:text-white transition-colors"
                                data-testid={`chat-quick-reply-${i}`}
                            >
                                {q}
                            </button>
                        ))}
                    </div>
                )}

                {handoffHref && (
                    <div className="pt-2" data-testid="chat-handoff-wrapper">
                        <Link
                            to={handoffHref}
                            onClick={onClose}
                            className="group inline-flex items-center justify-between gap-2 w-full bg-brand-coral hover:bg-[#d04438] text-white font-semibold uppercase tracking-wider text-xs px-4 h-11 transition-colors"
                            data-testid="chat-handoff-button"
                        >
                            <span>Start free quote</span>
                            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" strokeWidth={2.5} />
                        </Link>
                    </div>
                )}

                {error && (
                    <p className="text-[11px] text-brand-coral px-1" data-testid="chat-error">
                        {error}
                    </p>
                )}
            </div>

            {/* Composer */}
            {closed ? (
                <div
                    className="border-t border-brand-dark/10 bg-brand-offwhite px-4 py-3 text-xs text-brand-dark/70"
                    data-testid="chat-closed-banner"
                >
                    Session closed.{" "}
                    <a href="tel:+14372416287" className="text-brand-cyan font-semibold">
                        Call 437-241-6287
                    </a>{" "}
                    to keep going.
                </div>
            ) : (
                <ChatComposer
                    value={draft}
                    onChange={setDraft}
                    onSend={handleSend}
                    sending={sending}
                    disabled={closed}
                />
            )}
        </div>
    );
};

export default ChatPanel;
